import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { rupiah, formatDate } from "@/lib/format";
import { DatePicker } from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const CATEGORIES = ["Gaji Guru", "Operasional", "Sewa Tempat", "Peralatan", "Lainnya"];
const CATEGORY_COLORS = {
  "Gaji Guru": "bg-primary/10 text-primary hover:bg-primary/10",
  "Operasional": "bg-amber-100 text-amber-700 hover:bg-amber-100",
  "Sewa Tempat": "bg-sky-100 text-sky-700 hover:bg-sky-100",
  "Peralatan": "bg-emerald-100 text-emerald-700 hover:bg-emerald-100",
  "Lainnya": "bg-slate-200 text-slate-600 hover:bg-slate-200",
};

const today = () => new Date().toISOString().slice(0, 10);
const ACCOUNT_OPTIONS = ["Kas", "Bank", "Kas Kecil"];
const EMPTY = { category: "Operasional", description: "", amount: "", expense_date: today(), account: "Kas", teacher_id: "none" };

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/expenses").then((res) => setExpenses(res.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/teachers").then((res) => setTeachers(res.data.filter((t) => t.status === "Aktif"))).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/expenses", { ...form, amount: Number(form.amount) || 0,
        teacher_id: form.teacher_id === "none" ? null : form.teacher_id });
      toast.success("Pengeluaran tercatat");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(apiError(err, "Gagal mencatat pengeluaran"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (e) => {
    if (!window.confirm(`Hapus pengeluaran "${e.description}"?`)) return;
    try {
      await api.delete(`/expenses/${e.id}`);
      toast.success("Pengeluaran dihapus");
      load();
    } catch (err) {
      toast.error(apiError(err, "Gagal menghapus pengeluaran"));
    }
  };

  const total = expenses.reduce((sum, e) => sum + e.amount, 0);

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Pengeluaran</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {expenses.length} catatan • Total <span className="font-bold text-accent">{rupiah(total)}</span>
          </p>
        </div>
        <Button onClick={() => setOpen(true)} className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-expense-button">
          <Plus className="h-4 w-4" /> Catat Pengeluaran
        </Button>
      </div>

      <Card className="border-border shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tanggal</TableHead>
                <TableHead>Kategori</TableHead>
                <TableHead>Deskripsi</TableHead>
                <TableHead>Dicatat oleh</TableHead>
                <TableHead className="text-right">Jumlah</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {expenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-10 text-center text-muted-foreground" data-testid="expenses-empty">
                    Belum ada pengeluaran tercatat.
                  </TableCell>
                </TableRow>
              ) : expenses.map((e) => (
                <TableRow key={e.id} className="transition-colors duration-200 hover:bg-slate-50" data-testid={`expense-row-${e.id}`}>
                  <TableCell>{formatDate(e.expense_date)}</TableCell>
                  <TableCell>
                    <Badge className={CATEGORY_COLORS[e.category] || CATEGORY_COLORS["Lainnya"]}>{e.category}</Badge>
                  </TableCell>
                  <TableCell>
                    {e.description}
                    {e.teacher_name && <span className="block text-xs text-muted-foreground">Guru: {e.teacher_name}</span>}
                    <span className="block text-xs text-muted-foreground">Akun: {e.account || "Kas"}</span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{e.created_by || "-"}</TableCell>
                  <TableCell className="text-right font-bold text-accent">{rupiah(e.amount)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => remove(e)} data-testid={`delete-expense-${e.id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="expense-form-dialog">
          <DialogHeader>
            <DialogTitle>Catat Pengeluaran</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Kategori</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.category === "Gaji Guru" && (
              <div className="space-y-2">
                <Label>Guru (opsional)</Label>
                <Select value={form.teacher_id} onValueChange={(v) => setForm({ ...form, teacher_id: v })}>
                  <SelectTrigger data-testid="expense-teacher-select"><SelectValue placeholder="Pilih guru" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Tidak spesifik</SelectItem>
                    {teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Deskripsi *</Label>
              <Input placeholder="cth: Listrik & air bulan Juni" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="expense-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Jumlah (Rp) *</Label>
                <Input type="number" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount-input" />
              </div>
              <div className="space-y-2">
                <Label>Diambil dari Akun</Label>
                <Select value={form.account} onValueChange={(v) => setForm({ ...form, account: v })}>
                  <SelectTrigger data-testid="expense-account-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACCOUNT_OPTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Tanggal</Label>
              <DatePicker value={form.expense_date} onChange={(v) => setForm({ ...form, expense_date: v })} testId="expense-date-picker" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="expense-cancel-button">Batal</Button>
            <Button onClick={save} disabled={saving || !form.description || !form.amount} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="expense-save-button">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
